import { ChangeDetectorRef, Component, Input, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { InspectionCompanyService } from 'src/app/pages/inspection-company/services/inspection-company.service';
import { MediaGroup } from 'src/app/types/enums/media.enum';
import { IMediaInfo } from 'src/app/types/interfaces/common.interface';
import { getErrorMessage, isEmptyString } from '../../utils/helper';
import { BaseContentComponent } from '../base-content/base-content.component';
import { CommonModalService } from '../common-modal/common-modal.service';
import { MediaGalleryModalComponent } from '../media-gallery-modal/media-gallery-modal.component';
import { ModalUploadMediaComponent } from '../modal-upload-media/modal-upload-media.component';
import { ToastService } from '../toast/toast.service';
import { UploadImageBoxComponent } from '../upload-image-box/upload-image-box.component';

@Component({
  selector: 'app-upload-image-list',
  templateUrl: './upload-image-list.component.html',
  styleUrls: ['./upload-image-list.component.scss']
})
export class UploadImageListComponent extends BaseContentComponent {
  @ViewChild(ModalUploadMediaComponent) uploadModal: ModalUploadMediaComponent;
  @ViewChild(MediaGalleryModalComponent) galleryModal:MediaGalleryModalComponent;
  @ViewChildren(UploadImageBoxComponent) uploadBoxes: QueryList<UploadImageBoxComponent>;
  @Input() mode: 'input' | 'view' = 'view';
  @Input() mediaTitle: string;
  @Input() compactMode: boolean = false;
  @Input() showActions: boolean = false;
  @Input() maxItem?: number;
  @Input() dynamicPlaceholders: boolean = false;
  @Input() mediaGroup: MediaGroup;
  @Input() selectable: boolean = false;
  allSelected: boolean = false;

  get previewImages():IMediaInfo[] {
    const args = this.compactMode? [0, 4] : [];
    return Array.prototype.slice.call(this._mediaList ?? [], ...args);
  }
  _mediaList: IMediaInfo[];

  @Input()
  get mediaList(): IMediaInfo[] { return this._mediaList; }
  set mediaList(list: IMediaInfo[]) {
    this._mediaList = list;
  }

  refreshSelectAllCheckbox() {
    this.allSelected =
      this.uploadBoxes.find(b => b.hasFile()) != null &&  // there exists at least one box with a file
      !this.uploadBoxes.find(b => b.hasFile() && !b.selected); // AND of which there is not one that is not selected
    this.cdr.detectChanges();
  }

  anySelected() {
    return this.uploadBoxes?.find(b => b.selected);
  }

  confirmDeletion(actionOnConfirm: () => void) {
    const config = {
      title: `MODAL.TITLE.CONFIRM_DELETE_${this.mediaGroup === MediaGroup.VIDEO ? 'VIDEO' : 'IMAGE'}`,
      text:  `MODAL.TEXT.CONFIRM_DELETE_${this.mediaGroup === MediaGroup.VIDEO ? 'VIDEO' : 'IMAGE'}`,
      primaryBtnTxt: 'BUTTON.CONFIRM',
      secondaryBtnTxt: 'BUTTON.CANCEL',
    };
    const modalSub = this.commonModal.openWarningModal(config).subscribe((res: 'yes' | 'close') => {
      if (res && res === 'yes') {
        actionOnConfirm.apply(this);
        this.cdr.detectChanges();
      }
    });
    this.subscription.push(modalSub);
  }

  deleteSelected() {
    this.confirmDeletion(() => {
      const indexes = this.uploadBoxes?.filter(b => b.selected ).map(b => this.uploadBoxes.toArray().indexOf(b));
      this.bulkRemoveMedia(indexes);
    });
  }

  deleteMedia(index: number) {
    this.confirmDeletion(() => {
      this.bulkRemoveMedia([index]);
    });
  }

  /**
   *
   * @param indexes array of indexes of media to be removed
   */
  bulkRemoveMedia(indexes: number[]) {
    this.inspectionService.deleteMedia(
      indexes.filter(i => this.mediaList[i].file_upload_id ).map(i => this.mediaList[i].file_upload_id!.id)
    ).subscribe({
      next: res => {
        // 1. reverse sort indexes - delete must start from highest to lowest index
        indexes.sort().reverse().forEach(i => {
          this.mediaList[i].file = this.mediaList[i].file_upload_id = undefined;
          this.uploadBoxes.get(i)!.selected = false;

          // 2. then use splice for each index to modify the original array - if dynamicPlaceholders is true
          if (this.dynamicPlaceholders) this.mediaList.splice(i, 1);
        });

        // 3. if dynamicPlaceholders is true and at least one box was removed, append a new placeholder
        if (indexes.length > 0 && this.dynamicPlaceholders) {
          this.appendPlaceholderToMediaList();
        }

        // 4. select all checkbox needs to be refreshed
        this.refreshSelectAllCheckbox();
      },

      error: err => {
        this.toastService.error( getErrorMessage(err, this.translateService) );
      }
    })



  }

  toggleSelectAll(event: any) {
    this.uploadBoxes?.forEach(element => {
      // skip the empty ones
      if (!element.hasFile()) return;
      element.selected = event.currentTarget.checked;
    });
  }

  openMoreGallery() {
    this.galleryModal.openModal();
  }

  get viewMoreCount() {
    return (this.mediaList?.length ?? 0 ) - 4;
  }

  constructor(
    private activeRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private toastService: ToastService,
    private inspectionService: InspectionCompanyService,
    private commonModal: CommonModalService
    ) {
    super(activeRoute);
  }

  openImageViewer(index: number) {
    this.commonModal.openImageViewerModal(this.mediaList, index);
  }
  async openUploadModal(index: number) {
    this.uploadModal.openModal(this.mediaList[index]);
  }

  handleSuccessUpload(uploaded:IMediaInfo) {
    uploaded.error = undefined;
    if (this.dynamicPlaceholders && (this.maxItem && this.mediaList.length < this.maxItem)) {
      this.appendPlaceholderToMediaList();
    }

    if (uploaded.media_group === MediaGroup.VIDEO) {
      this.uploadBoxes.find(b => b.media === uploaded)?.reloadVideo();
    }
  }

  /**
   * @returns true if the last media detail has no title and no file
   */
  hasPlaceholderBox() {
    if (!this.mediaList || this.mediaList.length === 0) return false;
    const last = this.mediaList[this.mediaList.length - 1];
    return isEmptyString(last.title) && !last.file_upload_id;
  }

  appendPlaceholderToMediaList() {
    if (this.hasPlaceholderBox()) return;
    this.mediaList.push({
      media_group: this.mediaGroup,
    } as IMediaInfo);
  }
}
